<script setup lang="ts">
import { nextTick, onUnmounted, ref, watch } from 'vue';
import AppIcon from './AppIcon.vue';
import type { IconName } from './icons.js';

/**
 * A small actions menu. Items are `MenuItem` components in the slot. Closes on
 * outside pointer, Escape, Tab or after an item is chosen; arrows move focus.
 */
const props = defineProps<{
  label: string;
  disabled?: boolean;
  icon?: IconName | undefined;
  small?: boolean | undefined;
  align?: 'left' | 'right' | undefined;
}>();
const open = ref(false);
const root = ref<HTMLElement>();
const list = ref<HTMLElement>();
watch(
  () => props.disabled,
  (disabled) => {
    if (disabled) close();
  },
);

function items(): HTMLElement[] {
  return [...(list.value?.querySelectorAll<HTMLElement>('[role="menuitem"]:enabled') ?? [])];
}
async function toggle() {
  if (props.disabled) return;
  open.value = !open.value;
  if (open.value) {
    await nextTick();
    items()[0]?.focus();
  }
}
function close(restoreFocus = false) {
  if (!open.value) return;
  open.value = false;
  if (restoreFocus) root.value?.querySelector<HTMLElement>('button')?.focus();
}
function onDocumentPointer(event: PointerEvent) {
  if (!(event.target instanceof Node) || !root.value?.contains(event.target)) close();
}
function onKey(event: KeyboardEvent) {
  if (!open.value) return;
  const focusable = items();
  const index = focusable.indexOf(document.activeElement as HTMLElement);
  if (event.key === 'Escape') {
    event.preventDefault();
    close(true);
  } else if (event.key === 'ArrowDown') {
    event.preventDefault();
    focusable[(index + 1) % focusable.length]?.focus();
  } else if (event.key === 'ArrowUp') {
    event.preventDefault();
    focusable[(index - 1 + focusable.length) % focusable.length]?.focus();
  } else if (event.key === 'Tab') close();
}
watch(open, (value) => {
  if (value) document.addEventListener('pointerdown', onDocumentPointer, true);
  else document.removeEventListener('pointerdown', onDocumentPointer, true);
});
onUnmounted(() => document.removeEventListener('pointerdown', onDocumentPointer, true));
</script>

<template>
  <div ref="root" class="menu" :data-align="props.align ?? 'right'" @keydown="onKey">
    <button
      type="button"
      class="icon-button quiet"
      :class="{ small: props.small }"
      :aria-label="props.label"
      :title="props.label"
      :disabled="props.disabled"
      aria-haspopup="menu"
      :aria-expanded="open ? 'true' : 'false'"
      @click="toggle"
    >
      <AppIcon :name="props.icon ?? 'more'" />
    </button>
    <div
      v-if="open"
      ref="list"
      class="menu-list"
      role="menu"
      :aria-label="props.label"
      @click="close()"
    >
      <slot />
    </div>
  </div>
</template>
